import React, { useState, useEffect, useRef } from "react";
import { Grid, Skeleton, Checkbox, Tooltip, Chip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ButtonComponent from "../../Components/Common/button/Button";
import DataTable from "../../Components/Common/datatable/DataTable";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import BlackWhiteSwitch from "./BlackWhiteSwitch";

const BottlerMarketForecastTab = ({ data, email, isEmailValid }) => {
  const pcGridRef = useRef(null);

  const [pcBottlerOption, setPcBottlerOption] = useState([]);
  const [pcPartnerOption, setPcPartnerOption] = useState([]);
  const [profitCenterOption, setProfitCenterOption] = useState([]);
  const [bottlerOwnerCodeValue, setBottlerOwnerCodeValue] = useState("");

  const [selectedPCBottlers, setSelectedPCBottlers] = useState([]);
  const [selectedPCPartners, setSelectedPCPartners] = useState([]);
  const [selectedProfitCenters, setSelectedProfitCenters] = useState([]);

  const [filteredPCData, setFilteredPCData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pcSelectedRows, setPCSelectedRows] = useState([]);
  const [pcActionInFlight, setPcActionInFlight] = useState(false);

  const [searchPCPartnerText, setSearchPCPartnerText] = useState("");
  const [searchText, setSearchText] = useState("");

  const selectAllPcPartnerOption = {
    pcPartnerDetails: "All Bottler Owner Code",
    isSelectAll: true,
  };
  const selectAllProfitCenterOption = {
    profitCenterDetails: "All Customer Country",
    isSelectAll: true,
  };

  const optionsWithSelectAllProfitCenters =
    profitCenterOption.length > 0
      ? [selectAllProfitCenterOption, ...profitCenterOption]
      : [];

  const [profitCenterNoOptionsText, setProfitCenterNoOptionsText] = useState(
    "Select Bottler to view Customer Country",
  );

  useEffect(() => {
    fetchBCPUserAccessList();
  }, [data?.id]);

  //Table Data
  const fetchBCPUserAccessList = async () => {
    if (!data?.id) return;
    try {
      setLoading(true);
      const response = await ApiService.get(
        `/v1/user-pc-country-access/get-combination-list/marketforecast?userId=${data.id}`,
      );
      setFilteredPCData(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data.roles.includes("Bottler")) {
      fetchPCBottlersByUser();
    } else {
      fetchAllPCBottlerSelection();
    }
  }, []);

  // Bottler Role Bottler Options
  const fetchPCBottlersByUser = async () => {
    try {
      const response = await ApiService.get(
        `v1/user-pc-country-access/get-bottlers-by-user-id/marketforecast?userId=${data.id}`,
      );
      if (response.data && response.data.length > 0) {
        setPcBottlerOption(response.data); // Set bottler options if data is not empty
      }
    } catch (error) {
      console.error("Error fetching bottler selection:", error);
    }
  };

  // Non Bottler Role Bottler Options
  const fetchAllPCBottlerSelection = async () => {
    try {
      const response = await ApiService.get(
        `/v1/user-pc-country-access/get-active-bottler-list/marketforecast`,
      );
      setPcBottlerOption(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const updateProfitCenterOption = async (selectedPartners) => {
    if (!selectedPartners || selectedPartners.length === 0) {
      setProfitCenterOption([]);
      setProfitCenterNoOptionsText(
        "Select Bottler Owner Code to view Customer Country",
      );
      return;
    }

    const payloadData = {
      userId: data.id,
      bottlerOwnerCodeList: selectedPartners, // selectedPartners is already array of strings like ["9007505"]
    };

    try {
      const response = await ApiService.post(
        `/v1/user-pc-country-access/get-country-by-bottler-owner-code/marketforecast`,
        payloadData,
      );

      if (response.data && response.data.length > 0) {
        const profitCenterOptions = response.data.map((item) => ({
          ...item,
          profitCenterDetails: item.profitCenterDetails,
        }));

        setProfitCenterOption(profitCenterOptions);
        setProfitCenterNoOptionsText(
          "Select Bottler Owner Code to view Customer Country",
        );
      } else {
        setProfitCenterOption([]);
        setProfitCenterNoOptionsText(
          "No Customer Country found for selected Bottler Owner Code",
        );
      }
    } catch (error) {
      console.error("Error fetching Country options:", error);
      setProfitCenterOption([]);
      setProfitCenterNoOptionsText(
        "Error fetching Country options. Please try again.",
      );
    }
  };

  const handlePCBottlerChange = async (event, newValue) => {
    setSelectedPCBottlers(newValue || []);

    // Clear dependent selections
    setSelectedPCPartners([]);
    setSelectedProfitCenters([]);
    setProfitCenterOption([]);
    setSearchPCPartnerText("");

    if (newValue && newValue.length > 0) {
      try {
        const response = await ApiService.get(
          `/v1/user-pc-country-access/get-bottler-owner-code-by-bottler?bottler=${encodeURIComponent(newValue)}`,
        );

        setPcPartnerOption(response.data || []);
        setBottlerOwnerCodeValue(
          Array.isArray(response.data) ? response.data.join(", ") : "",
        );
        setSelectedPCPartners(response.data || []);
        updateProfitCenterOption(response.data || []);
      } catch (error) {
        console.error("Error fetching partner options:", error);
        setBottlerOwnerCodeValue("");
      }
    } else {
      setPcPartnerOption([]);
      setBottlerOwnerCodeValue("");
    }
  };

  const handlePcEnable = async () => {
    if (pcActionInFlight) return;

    if (email === "") {
      MessagePopup({
        icon: "error",
        title: "Email Required",
        message: "Please enter a Email address before proceeding.",
      });
      return;
    }

    setPcActionInFlight(true);
    if (
      !selectedPCBottlers ||
      selectedPCBottlers.length === 0 ||
      !selectedPCPartners ||
      selectedPCPartners.length === 0 ||
      !selectedProfitCenters ||
      selectedProfitCenters.length === 0
    ) {
      MessagePopup({
        icon: "error",
        title: "Selection Failed",
        message:
          "To proceed, please choose a Bottler, Bottler Owner Code, and Customer Country.",
      });
      return;
    }

    const accessCombinationDtoList = [
      {
        bottlerName: selectedPCBottlers,
        bottlerOwnerCode: String(selectedPCPartners),
        profitCenterIdList: selectedProfitCenters.map(
          (pc) => pc.profitCenterDetails.split("-")[0],
        ),
      },
    ];

    const result = await MessagePopup({
      title: "Confirm User Enable",
      html: `<p>Are you sure you want to Enable the user ? </p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) {
      setPcActionInFlight(false);
      return;
    }

    const requestBody = {
      userId: data.id,
      role: data.roles,
      action: "ADD",
      accessCombinationDtoList,
    };

    try {
      const response = await ApiService.post(
        `/v1/user-pc-country-access/manage-access/marketforecast`,
        requestBody,
      );

      MessagePopup({
        icon: "success",
        title: "Success",
        message: response.data,
      });

      setSelectedPCBottlers([]);
      setSelectedPCPartners([]);
      setSelectedProfitCenters([]);
      setPcPartnerOption([]);
      setProfitCenterOption([]);
      setSearchText("");
      setSearchPCPartnerText("");
      setBottlerOwnerCodeValue("");
      fetchBCPUserAccessList();

      if (data.roles.includes("Bottler")) {
        fetchPCBottlersByUser();
      }
    } catch (error) {
      console.error("Error adding role:", error);
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to add user role. Please try again.",
      });
    } finally {
      setPcActionInFlight(false);
    }
  };

  console.log(pcSelectedRows, "pcSelectedRows");
  const handlePcDisable = async () => {
    if (pcActionInFlight) return;

    if (pcSelectedRows.length === 0) {
      MessagePopup({
        icon: "error",
        title: "No Selection",
        message: "Please select at least one row to delete.",
      });
      return;
    }

    setPcActionInFlight(true);

    const accessMap = {};

    pcSelectedRows.forEach((row) => {
      const { bottler, bottlerOwnerCode, pcCountryId } = row;

      const key = `${bottler}__${String(bottlerOwnerCode)}`;

      if (!accessMap[key]) {
        accessMap[key] = {
          bottlerName: bottler,
          bottlerOwnerCode: String(bottlerOwnerCode),
          profitCenterIdSet: new Set(),
        };
      }
      accessMap[key].profitCenterIdSet.add(String(pcCountryId));
    });

    const accessCombinationDtoList = Object.values(accessMap).map(
      ({ bottlerName, bottlerOwnerCode, profitCenterIdSet }) => ({
        bottlerName,
        bottlerOwnerCode,
        profitCenterIdList: Array.from(profitCenterIdSet),
      }),
    );

    const requestBody = {
      userId: data.id,
      role: data.roles,
      action: "DELETE",
      accessCombinationDtoList,
    };

    // Confirmation popup
    const result = await MessagePopup({
      title: "Confirm User Disable",
      html: `<p>Are you sure you want to disable the selected user access?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) {
      setPcActionInFlight(false);
      return;
    }

    try {
      const response = await ApiService.post(
        `/v1/user-pc-country-access/manage-access/marketforecast`,
        requestBody,
      );

      MessagePopup({
        icon: "success",
        title: "Success",
        message: response.data,
      });

      setSelectedPCBottlers([]);
      setSelectedPCPartners([]);
      setSelectedProfitCenters([]);
      setPcPartnerOption([]);
      setProfitCenterOption([]);
      setPCSelectedRows([]);
      setBottlerOwnerCodeValue("");

      fetchBCPUserAccessList();
      if (data.roles.includes("Bottler")) {
        fetchPCBottlersByUser();
      }
    } catch (error) {
      console.error("Error removing role:", error);
      const errorMessage =
        error.response?.data || "Failed to Disable user. Please try again.";
      // const errorMessage = "Failed to Disable user. Please try again.";
      MessagePopup({
        icon: "error",
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setPcActionInFlight(false);
    }
  };

  const pcColumnDefinition = [
    {
      headerName: "",
      checkboxSelection: true,
      width: 40,
      minWidth: 40,
      pinned: "left",
      suppressMenu: true,
      suppressMovable: true,
      headerCheckboxSelection: true,
    },
    {
      field: "bottler",
      headerName: "Bottler",
      sortable: true,
      unSortIcon: true,
      width: 150,
      minWidth: 150,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "bottlerOwnerCode",
      headerName: "Bottler Owner Code",
      sortable: true,
      unSortIcon: true,
      width: 370,
      minWidth: 370,
      valueGetter: (params) => {
        const { bottlerOwnerCode } = params.data;
        return `${bottlerOwnerCode || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "pcCountryName",
      headerName: "Customer Country",
      sortable: true,
      unSortIcon: true,
      width: 370,
      minWidth: 370,
      valueGetter: (params) => {
        const { pcCountryId, pcCountryDescription } = params.data;
        return `${pcCountryId || ""} - ${pcCountryDescription || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "isActive",
      headerName: "Notification",
      width: 160,
      minWidth: 160,
      sortable: false,
      filter: false,
      headerComponent: (props) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>Notification</span>
          <input
            type="checkbox"
            style={{
              width: 20,
              height: 20,
              accentColor: "lightgrey",
              backgroundColor: "white",
            }}

            checked={filteredPCData.every((item) => item.isActive)}
            onChange={async (event) => {
              const allowMail = event.target.checked;


              const accessMap = {};

              filteredPCData.forEach((item) => {
                const key = `${item.bottler}__${String(item.bottlerOwnerCode)}`;

                if (!accessMap[key]) {
                  accessMap[key] = {
                    bottlerName: item.bottler,
                    bottlerOwnerCode: String(item.bottlerOwnerCode),
                    profitCenterSet: new Set(),
                  };
                }

                accessMap[key].profitCenterSet.add(String(item.pcCountryId));
              });

              const accessCombinationDtoList = Object.values(accessMap).map(
                ({ bottlerName, bottlerOwnerCode, profitCenterSet }) => ({
                  bottlerName,
                  bottlerOwnerCode,
                  profitCenterIdList: Array.from(profitCenterSet),
                }),
              );

              const requestBody = {
                userId: data.id,
                role: data.roles,
                allowMail,
                accessCombinationDtoList,
              };

              try {
                await ApiService.post(
                  "/v1/user-pc-country-access/update-bulk-mail-access",
                  requestBody,
                );

                fetchBCPUserAccessList();

                MessagePopup({
                  icon: "success",
                  title: "Success",
                  message: `All notifications turned ${allowMail ? "ON" : "OFF"
                    } successfully.`,
                });
              } catch (error) {
                console.error("Bulk update error:", error);

                MessagePopup({
                  icon: "error",
                  title: "Error",
                  message:
                    error.response?.data ||
                    "Failed to update all notifications.",
                });
              }
            }}
          />
        </div>
      ),

      cellRenderer: (params) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 13, marginRight: 4 }}>OFF</span>
          <BlackWhiteSwitch
            checked={params.data.isActive}
            onChange={async (event) => {
              const isChecked = event.target.checked;

              try {
                await ApiService.post(
                  "/v1/user-pc-country-access/update-mail-access",
                  {
                    userId: data.id,
                    bottlerOwnerCode: params.data.bottlerOwnerCode,
                    profitCenterId: params.data.pcCountryId,
                    isActive: isChecked,
                  },
                );
                params.node.setDataValue("isActive", isChecked);
              } catch (error) {
                console.error("Error updating notification:", error);
                const errorMessage =
                  error.response?.data ||
                  "Failed to update notification. Please try again.";

                MessagePopup({
                  icon: "error",
                  title: "Error",
                  message: errorMessage,
                });
              }
            }}
          />
          <span style={{ fontSize: 13, marginLeft: 4 }}>ON</span>
        </div>
      ),
    },
  ];

  const onPCGridReady = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();


      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  useEffect(() => {
    if (
      pcGridRef.current &&
      pcGridRef.current.api &&
      filteredPCData.length > 0
    ) {
      pcGridRef.current.api.sizeColumnsToFit();
    }
  }, [filteredPCData]);

  const onPCSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setPCSelectedRows(selected);
  };

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  return (
    <>
      <Grid
        container
        spacing={0.5}
        alignItems="center"
        marginTop={2}
        marginBottom={2}
      >
        <Grid item xs={12} sm={2}>
          <Autocomplete
            options={pcBottlerOption}
            getOptionLabel={(option) => option}
            value={selectedPCBottlers}
            onChange={handlePCBottlerChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bottler *"
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: {
                    transform: "translate(14px, -14px) scale(0.75)",
                  },
                }}
              />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Bottler Owner Code"
            variant="outlined"
            size="small"
            value={bottlerOwnerCodeValue}
            disabled
            fullWidth
            InputLabelProps={{
              shrink: true,
              style: {
                transform: "translate(14px, -14px) scale(0.75)",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            limitTags={1}
            id="pc-country-autocomplete"
            options={optionsWithSelectAllProfitCenters}
            getOptionLabel={(option) => option.profitCenterDetails}
            value={selectedProfitCenters}
            onChange={(event, newValue, reason) => {
              const isSelectAllClicked = newValue.some(
                (item) => item.isSelectAll,
              );
              if (isSelectAllClicked) {
                if (
                  selectedProfitCenters.length ===
                  optionsWithSelectAllProfitCenters.length - 1
                ) {
                  setSelectedProfitCenters([]);
                } else {
                  setSelectedProfitCenters(
                    optionsWithSelectAllProfitCenters.filter(
                      (item) => !item.isSelectAll,
                    ),
                  );
                }
                setSearchText(""); // Clear search text after Select All action
              } else {
                setSelectedProfitCenters(newValue);
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              // Update search text only when typing or clearing the input
              if (reason === "input" || reason === "clear") {
                setSearchText(newInputValue);
              }
            }}
            inputValue={searchText} // Control the input value
            disableCloseOnSelect
            filterSelectedOptions={false}
            noOptionsText={profitCenterNoOptionsText}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={
                    option.isSelectAll
                      ? selectedProfitCenters.length ===
                      optionsWithSelectAllProfitCenters.length - 1
                      : selected
                  }
                />
                {option.profitCenterDetails}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer Country *"
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                  style: {
                    transform: "translate(14px, -14px) scale(0.75)",
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    maxHeight: 120,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    flexDirection: "row",
                    overflow: "auto",
                  },
                  "& .MuiAutocomplete-input": {
                    order: -1,
                    minWidth: "100px",
                    flexGrow: 1,
                    position: "relative",
                  },
                  "& .MuiChip-root": {
                    order: 0,
                  },
                }}
              />
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  key={option.profitCenterDetails}
                  label={option.profitCenterDetails}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </Grid>
        <Grid item xs={12} sm={1} display="flex" justifyContent="flex-end">
          <ButtonComponent
            bgColor="black"
            color="white"
            label="Enable"
            fullWidth
            onClick={handlePcEnable}
            disabled={pcActionInFlight}
          />
        </Grid>
        <Grid item xs={12} sm={1} display="flex" justifyContent="flex-end">
          <ButtonComponent
            bgColor="black"
            color="white"
            label="Disable"
            fullWidth
            onClick={handlePcDisable}
            disabled={pcActionInFlight}
          />
        </Grid>
      </Grid>
      {loading ? (
        <Skeleton
          variant="rectangular"
          height={"68vh"}
          animation="pulse"
          style={{ marginBottom: "16px", borderRadius: "8px" }}
        />
      ) : (
        <DataTable
          ref={pcGridRef}
          columns={pcColumnDefinition}
          rows={filteredPCData || []}
          tableHeight={"68vh"}
          suppressMovableColumns={true}
          suppressHorizontalScroll={false}
          suppressPaginationPanel={false}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          onGridReady={onPCGridReady}
          rowSelection="multiple"
          onSelectionChanged={onPCSelectionChanged}
        />
      )}
    </>
  );
};

export default BottlerMarketForecastTab;
